/**
 * Signs operators in with or without data. With data, the server checks the PIN (and its lockout);
 * with none, the tablet checks it against the PIN checks in its offline pack, with the same rules:
 * hospital operators only in this robot's hospital, service accounts everywhere, 5 wrong codes
 * lock the keypad for 15 minutes, and mistyped hospital codes never lock a service account out.
 * Every offline sign-in and failure is kept and uploaded later.
 */
import type { SessionResponse } from '@evesix/contracts';
import { OFFLINE_PIN_CHECK, SERVICE_SCOPE } from '@evesix/contracts/offline';
import type { PinVerification } from '../domain/types';
import { ApiRequestError, type ApiClient } from './api';
import { realCyclesOf, type AuthService } from './auth';
import type { LocalStore } from './local-store';
import type { SessionState } from './session';
import type { SyncService } from './sync';

const FAILURES_KEY = 'signin.failures';

type AccountKind = 'hospital' | 'service';

interface Failure {
  readonly at: number;
  readonly kind: AccountKind;
}

const encoder = new TextEncoder();

/** Must match the server's offlinePinCheck (packages/contracts/src/offline.ts). */
export async function offlinePinCheck(scope: string, pin: string): Promise<string> {
  const key = await crypto.subtle.importKey('raw', encoder.encode(pin), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt: encoder.encode(OFFLINE_PIN_CHECK.salt(scope)),
      iterations: OFFLINE_PIN_CHECK.iterations,
    },
    key,
    256,
  );
  return btoa(String.fromCharCode(...new Uint8Array(bits)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export interface OfflineAuthOptions {
  readonly client: ApiClient;
  /** Its robot (and hospital, for whose operators may sign in) come from the tablet's setup. */
  readonly sync: SyncService;
  readonly store: LocalStore;
  readonly session: SessionState;
  readonly now?: () => number;
  readonly uuid?: () => string;
}

export class OfflineAuthService implements AuthService {
  readonly #client: ApiClient;
  readonly #sync: SyncService;
  readonly #store: LocalStore;
  readonly #session: SessionState;
  readonly #now: () => number;
  readonly #uuid: () => string;

  constructor({
    client,
    sync,
    store,
    session,
    now = Date.now,
    uuid = () => crypto.randomUUID(),
  }: OfflineAuthOptions) {
    this.#client = client;
    this.#sync = sync;
    this.#store = store;
    this.#session = session;
    this.#now = now;
    this.#uuid = uuid;
  }

  async verifyPin(pin: string): Promise<PinVerification> {
    await this.#sync.ready;
    const unitId = this.#sync.robotId;
    if (!unitId) return { ok: false, reason: 'unavailable' }; // not set up for a robot yet
    let online: SessionResponse;
    try {
      online = await this.#client.post<SessionResponse>('/sessions', { unitId, pin });
    } catch (error) {
      if (error instanceof ApiRequestError && error.status < 500) {
        if (error.code === 'invalid_pin') return { ok: false, reason: 'invalid' };
        if (error.code === 'locked') {
          return { ok: false, reason: 'locked', retryAfterSeconds: error.retryAfterSeconds ?? 900 };
        }
        throw error;
      }
      // No data, or the server is down: check the PIN here.
      return this.#verifyOffline(pin);
    }

    this.#client.setToken(online.token);
    this.#session.set({
      id: this.#uuid(),
      operatorId: online.operator.id,
      operatorName: online.operator.name,
      service: online.operator.service,
      offline: false,
      startedAt: new Date(this.#now()).toISOString(),
    });
    await this.#store.remove(FAILURES_KEY);
    void this.#sync.syncNow();
    return {
      ok: true,
      operator: { name: online.operator.name, realCycles: realCyclesOf(online.operator) },
    };
  }

  async signOut(): Promise<void> {
    const session = this.#session.current;
    this.#session.clear();
    if (session?.offline) await this.#event('logout_offline', session.operatorId);
    if (!this.#client.signedIn) return;
    try {
      await this.#client.delete('/sessions/current');
    } catch {
      // Offline: the server session simply expires.
    } finally {
      this.#client.setToken(null);
    }
  }

  async #verifyOffline(pin: string): Promise<PinVerification> {
    await this.#sync.ready;
    const pack = this.#sync.pack;
    if (!pack) return { ok: false, reason: 'unavailable' };

    const now = this.#now();
    const windowMs = pack.lockout.windowSeconds * 1000;
    const failures = ((await this.#store.get<Failure[]>(FAILURES_KEY)) ?? []).filter(
      (f) => now - f.at < windowMs,
    );
    const unlockAt = (kind: AccountKind) => {
      const ofKind = failures.filter((f) => f.kind === kind);
      if (ofKind.length < pack.lockout.maxFailures) return null;
      return Math.min(...ofKind.map((f) => f.at)) + windowMs;
    };
    const hospitalLock = unlockAt('hospital');
    const serviceLock = unlockAt('service');

    const [hospitalCheck, serviceCheck] = await Promise.all([
      offlinePinCheck(String(pack.robot.hospitalId), pin),
      offlinePinCheck(SERVICE_SCOPE, pin),
    ]);
    const operator = pack.operators.find(
      (o) => o.pinCheck === (o.service ? serviceCheck : hospitalCheck),
    );
    const kind: AccountKind = operator?.service ? 'service' : 'hospital';
    const lockedUntil = kind === 'service' ? serviceLock : hospitalLock;

    if (operator && lockedUntil === null) {
      const session = {
        id: this.#uuid(),
        operatorId: operator.id,
        operatorName: operator.name,
        service: operator.service,
        offline: true,
        startedAt: new Date(now).toISOString(),
      };
      this.#session.set(session);
      await this.#store.remove(FAILURES_KEY);
      await this.#store.addToOutbox({
        id: session.id,
        kind: 'session',
        state: 'pending',
        createdAt: session.startedAt,
        body: {
          id: session.id,
          operatorId: operator.id,
          startedAt: session.startedAt,
          endedAt: null,
          offline: true,
        },
      });
      return {
        ok: true,
        operator: {
          name: operator.name,
          realCycles: !operator.service || operator.mayRunRealCycles,
        },
      };
    }

    if (!operator) {
      // Typed while hospital codes are locked: only a service code could have got in.
      const counted: AccountKind = hospitalLock === null ? 'hospital' : 'service';
      await this.#store.put(FAILURES_KEY, [...failures, { at: now, kind: counted }]);
      await this.#event('login_failed_offline', null, { counted });
    }
    const lock = lockedUntil ?? hospitalLock;
    if (lock !== null) {
      return {
        ok: false,
        reason: 'locked',
        retryAfterSeconds: Math.max(1, Math.ceil((lock - now) / 1000)),
      };
    }
    return { ok: false, reason: 'invalid' };
  }

  async #event(type: string, operatorId: number | null, detail: Record<string, unknown> = {}) {
    const id = this.#uuid();
    const at = new Date(this.#now()).toISOString();
    await this.#store.addToOutbox({
      id,
      kind: 'event',
      state: 'pending',
      createdAt: at,
      body: { id, at, type, operatorId, cycleId: null, detail },
    });
  }
}
